import IconSprite from '../components/IconSprite'
import SiteFooter from '../components/SiteFooter'
import SiteHeader from '../components/SiteHeader'
import { PLANS } from '../plans'
import useReveal from '../useReveal'
import useWheelScroll from '../useWheelScroll'
import s from './Landing.module.css'

type Stat = { k: string; v: string; d: string; note?: string }
type Agent = { name: string; icon: string; running: boolean }
type Feature = { icon: string; title: string; lines: string[]; link: string }

const RAIL = ['i-home', 'i-chat', 'i-doc', 'i-upload', 'i-image', 'i-gear']

const TRUST = [
  { icon: 'ico-check', text: '카드 등록 없이 시작' },
  { icon: 'ico-bolt', text: '3분 만에 온보딩 완료' },
  { icon: 'ico-lock', text: '데이터는 안전하게 보호' },
]

const STATS: Stat[] = [
  { k: '이번 달 매출', v: '₩ 1,250,000', d: '↑ 18.7%', note: '(전월 대비)' },
  { k: '신규 고객', v: '32명', d: '↑ 14.3%' },
  { k: '전환율', v: '2.8%', d: '↑ 0.6%' },
  { k: '마케팅 ROAS', v: '3.2', d: '↑ 15.8%' },
]

const AGENTS: Agent[] = [
  { name: '마케팅 에이전트', icon: 'ag-megaphone', running: true },
  { name: '데이터 분석 에이전트', icon: 'ag-chart', running: true },
  { name: '고객 관리 에이전트', icon: 'ag-user', running: false },
  { name: '업무 정리 에이전트', icon: 'ag-clipboard', running: true },
]

const FEATURES: Feature[] = [
  {
    icon: 'ico-bot',
    title: '여러 AI 에이전트가 한 팀처럼',
    lines: [
      '마케팅, 분석, 고객 관리, 업무 정리까지',
      '각 분야의 AI 에이전트가 유기적으로 협업해',
      '당신의 비즈니스를 성장시킵니다.',
    ],
    link: 'AI 에이전트 둘러보기 →',
  },
  {
    icon: 'ico-bars',
    title: '데이터 기반 의사결정',
    lines: [
      '매출, 광고, 고객 데이터를 실시간으로 분석해',
      '핵심 인사이트와 실행 가이드를',
      '제공합니다.',
    ],
    link: '분석 기능 자세히 보기 →',
  },
  {
    icon: 'ico-checktile',
    title: '반복 업무 자동화',
    lines: [
      '보고서 작성, 문의 답변, 일정 관리 등',
      '반복적인 업무를 자동화하여',
      '더 중요한 일에 집중하세요.',
    ],
    link: '자동화 기능 보기 →',
  },
  {
    icon: 'ico-shield',
    title: '안전하고 신뢰할 수 있는 서비스',
    lines: [
      '업계 최고 수준의 보안으로 데이터를 보호하고',
      '안정적인 시스템으로 비즈니스를',
      '안심하고 운영하세요.',
    ],
    link: '보안 및 정책 보기 →',
  },
]

/** 줄바꿈이 디자인의 일부라서 <br>로 유지 */
function Lines({ lines }: { lines: string[] }) {
  return (
    <>
      {lines.map((line, i) => (
        <span key={line}>
          {i > 0 && <br />}
          {line}
        </span>
      ))}
    </>
  )
}

export default function Landing() {
  const pgrid = useWheelScroll<HTMLDivElement>()
  const fgrid = useReveal<HTMLDivElement>()
  const hgrid = useReveal<HTMLDivElement>()

  return (
    <div className={s.page}>
      <IconSprite />

      <SiteHeader />

      <section className={s.hero} id="home">
        <div className={`${s.wrap} ${s.hgrid}`} ref={hgrid} data-reveal>
          <div className={s.hcopy}>
            <div className={s.badge}>
              <img className={s.spark} src="/assets/ico-spark.png" alt="" />
              1인 사업자를 위한 AI 비즈니스 파트너
            </div>
            <h1>
              <span className={s.g}>AI 오토팀</span>
              <br />
              AI 비즈니스 서비스
            </h1>
            <p className={s.lead}>
              기획부터 마케팅, 고객 관리, 데이터 분석까지
              <br />
              여러 AI 에이전트가 당신의 사업 성장을 함께합니다.
            </p>
            <div className={s.cta}>
              <a className={`${s.btn} ${s.btnPrimary}`} href="#/signup">
                무료로 시작하기 →
              </a>
            </div>
            <div className={s.trust}>
              {TRUST.map((t) => (
                <span key={t.text}>
                  <img src={`/assets/${t.icon}.png`} alt="" />
                  {t.text}
                </span>
              ))}
            </div>
          </div>

          <div className={s.dash}>
            <div className={s.rail}>
              <img
                src="/assets/logo-mark.png"
                alt=""
                width={26}
                height={26}
                style={{ marginBottom: 8, objectFit: 'contain' }}
              />
              {RAIL.map((id, i) => (
                <div key={id} className={`${s.ic} ${i === 0 ? s.on : ''}`}>
                  <svg>
                    <use href={`#${id}`} />
                  </svg>
                </div>
              ))}
            </div>

            <div className={s.dbody}>
              <div className={s.dhead}>
                <div>
                  <h2>안녕하세요, 김대표님!</h2>
                  <p>오늘도 사업 성장을 응원해요! 💪</p>
                </div>
                <button className={s.dl}>
                  <svg>
                    <use href="#i-download" />
                  </svg>
                  리포트 다운로드
                </button>
              </div>

              <div className={s.stats}>
                {STATS.map((st) => (
                  <div key={st.k} className={s.stat}>
                    <div className={s.k}>{st.k}</div>
                    <div className={s.v}>{st.v}</div>
                    <div className={s.d}>
                      {st.d} {st.note && <em>{st.note}</em>}
                    </div>
                  </div>
                ))}
              </div>

              <div className={s.panes}>
                <div className={s.pane}>
                  <div className={s.top}>
                    <h3>매출 추이</h3>
                  </div>
                  <svg
                    className={s.chart}
                    viewBox="0 0 360 290"
                    preserveAspectRatio="xMidYMid meet"
                    fontFamily="inherit"
                  >
                    <defs>
                      <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
                        <stop stopColor="#4E8574" stopOpacity=".22" />
                        <stop offset="1" stopColor="#4E8574" stopOpacity="0" />
                      </linearGradient>
                      <filter id="tipsh" x="-30%" y="-30%" width="160%" height="180%">
                        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#16261F" floodOpacity=".12" />
                      </filter>
                    </defs>
                    {/* grid + y labels : 1500만=30 / 1000만=102 / 500만=173 / 0=245 */}
                    <g stroke="#E9F0EC" strokeWidth="1">
                      <line x1="56" y1="30" x2="352" y2="30" />
                      <line x1="56" y1="102" x2="352" y2="102" />
                      <line x1="56" y1="173" x2="352" y2="173" />
                      <line x1="56" y1="245" x2="352" y2="245" />
                    </g>
                    <g fill="#9AA8A3" fontSize="9.5" textAnchor="end">
                      <text x="48" y="33">1,500만</text>
                      <text x="48" y="105">1,000만</text>
                      <text x="48" y="176">500만</text>
                      <text x="48" y="248">0</text>
                    </g>
                    {/* series: 310 / 660 / 600 / 880 / 600 / 820 / 950 (만원) */}
                    <path
                      d="M64 200.6 L111 150.4 L158 159 L205 118.9 L251 159 L298 127.5 L344 108.8 L344 245 L64 245Z"
                      fill="url(#area)"
                    />
                    <g stroke="#BCD6CB" strokeWidth="1">
                      <line x1="64" y1="30" x2="64" y2="245" />
                      <line x1="344" y1="30" x2="344" y2="245" />
                    </g>
                    <polyline
                      points="64,200.6 111,150.4 158,159 205,118.9 251,159 298,127.5 344,108.8"
                      fill="none"
                      stroke="#3A6F61"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <g fill="#fff" stroke="#3A6F61" strokeWidth="2.2">
                      <circle cx="64" cy="200.6" r="3.8" />
                      <circle cx="111" cy="150.4" r="3.8" />
                      <circle cx="158" cy="159" r="3.8" />
                      <circle cx="205" cy="118.9" r="3.8" />
                      <circle cx="251" cy="159" r="3.8" />
                      <circle cx="298" cy="127.5" r="3.8" />
                    </g>
                    <circle cx="344" cy="108.8" r="5.4" fill="#fff" stroke="#3A6F61" strokeWidth="2.8" />
                    {/* tooltip */}
                    <g filter="url(#tipsh)">
                      <path
                        d="M234 34h112a10 10 0 0 1 10 10v34a10 10 0 0 1-10 10h-8l-5 10-5-10h-94a10 10 0 0 1-10-10V44a10 10 0 0 1 10-10z"
                        fill="#fff"
                      />
                    </g>
                    <text x="246" y="54" fill="#869390" fontSize="12">6/16</text>
                    <circle cx="250" cy="71" r="3.2" fill="#3A6F61" />
                    <text x="259" y="76" fill="#121F1C" fontSize="13.5" fontWeight="700">₩ 1,250,000</text>
                    <g fill="#9AA8A3" fontSize="9.5" textAnchor="middle">
                      <text x="64" y="272">5/12</text>
                      <text x="120" y="272">5/19</text>
                      <text x="176" y="272">5/26</text>
                      <text x="232" y="272">6/2</text>
                      <text x="288" y="272">6/9</text>
                      <text x="344" y="272">6/16</text>
                    </g>
                  </svg>
                </div>

                <div className={s.pane}>
                  <div className={s.top}>
                    <h3>AI 에이전트 현황</h3>
                    <span className={s.more}>전체 보기 ›</span>
                  </div>
                  {AGENTS.map((a) => (
                    <div key={a.name} className={`${s.agent} ${a.running ? '' : s.wait}`}>
                      <div className={s.ai}>
                        <img src={`/assets/${a.icon}.png`} alt="" />
                      </div>
                      <div className={s.nm}>{a.name}</div>
                      <div className={s.st}>
                        <i className={`${s.dot} ${a.running ? '' : s.wait}`} />
                        {a.running ? '실행 중' : '대기 중'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={s.features} id="features">
        <div className={`${s.wrap} ${s.fgrid}`} ref={fgrid} data-reveal>
          {FEATURES.map((f) => (
            <div key={f.title} className={s.fcard}>
              <div className={s.fhead}>
                <img className={s.fic} src={`/assets/${f.icon}.png`} alt="" />
                <h3>{f.title}</h3>
              </div>
              <p>
                <Lines lines={f.lines} />
              </p>
              <a href="#">{f.link}</a>
            </div>
          ))}
        </div>
      </section>

      <section className={s.plans} id="plans">
        <div className={s.wrap}>
          <h2 className={s.ptitle}>플랜</h2>
          <p className={s.psub}>무료로 시작하고, 필요해질 때 올리세요. 약정 없이 언제든 해지됩니다.</p>

          {/* 랜딩은 Free·Standard·Pro만. Trial은 플랜 페이지 비교표에서 보여준다. */}
          <div className={s.pgrid} ref={pgrid}>
            {PLANS.filter((p) => p.name !== 'Trial').map((p) => (
              <div key={p.name} className={`${s.pcard} ${p.best ? s.best : ''}`}>
                {p.best && <span className={s.badgeBest}>가장 많이 선택</span>}
                <h3>{p.name}</h3>
                <p className={s.pdesc}>{p.desc}</p>
                <div className={s.price}>
                  {p.price}
                  {p.unit && <em>{p.unit}</em>}
                </div>
                {p.note && <span className={s.trial}>{p.note}</span>}
                <ul className={s.plist}>
                  {p.items.map((it) => (
                    <li key={it}>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="m5 12.5 4.5 4.5L19 7.5" />
                      </svg>
                      {it}
                    </li>
                  ))}
                </ul>
                <a className={`${s.btn} ${p.best ? s.btnPrimary : s.btnGhost}`} href="#/signup">
                  {p.cta}
                </a>
              </div>
            ))}
          </div>

          <p className={s.pmore}>
            <a href="#/plans">플랜별 상세 비교표 보기 →</a>
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
